import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiSearch, 
  FiPlus, 
  FiX,
  FiCheck,
  FiAlertTriangle,
  FiEye,
  FiFilter,
  FiInfo,
  FiUser,
  FiCalendar,
  FiEdit3,
  FiTrash2,
  FiChevronDown
} from 'react-icons/fi';
import * as AllergyService from '../../services/allergyService';
import TopHeader from "../../../../common/Header/TopHeader";
import PatientMedicalClinicalData from '../../../../Patient/listPrincipalComponent/TableComponent/PatientMedicalClinicalData';
import PatientManagement from '../../../../common/Menu/PatientManagement';

const AllergiesTreatments = ({ onCountUpdate, componentName = 'AllergiesTreatments' }) => {
  const location = useLocation();
  
  // **OBTENER DATOS DEL PACIENTE DESDE NAVIGATION STATE**
  const patientData = location.state;
  const patientId = patientData?.patientId;

  const [allergies, setAllergies] = useState([]);
  const [filteredAllergies, setFilteredAllergies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [alertInfo, setAlertInfo] = useState(null);
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [selectedAllergy, setSelectedAllergy] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [showEditOffcanvas, setShowEditOffcanvas] = useState(false);
  const [editingAllergy, setEditingAllergy] = useState(null);
  
  // Estado para Add New offcanvas
  const [showAddOffcanvas, setShowAddOffcanvas] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    allergyType: '',
    severity: '',
    currentStatus: '',
    reportedDate: '',
    endDate: '',
    status: 'All'
  });

  // **FUNCIÓN PRINCIPAL PARA CARGAR DATOS**
  const loadAllergies = useCallback(async () => {
    if (!patientId) {
    console.log('No patientId provided, component will show empty state');
    setAllergies([]);
    onCountUpdate && onCountUpdate(componentName, 0);
    return;
  }
    
    setIsLoading(true);
    try {
      console.log('Loading allergies for patient:', patientId);
      const response = await AllergyService.getAllergiesByPatient(patientId);
      console.log('Raw API response:', response);
      
      let allergiesData = [];
      
      if (response.data) {
        allergiesData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        allergiesData = response;
      } else if (response.allergies) {
        allergiesData = Array.isArray(response.allergies) ? response.allergies : [response.allergies];
      }

      const normalizedAllergies = allergiesData.map(allergy => {
        console.log('Processing allergy:', allergy);
        
        // Handle patient information (could be populated or just ID)
        const patientInfo = typeof allergy.patient === 'object' && allergy.patient 
          ? { id: allergy.patient._id, name: allergy.patient.name }
          : { id: allergy.patient || patientId, name: patientData.patientName || 'Unknown Patient' };

        // Handle addedBy information (could be populated or just ID)
        const addedByInfo = typeof allergy.addedby === 'object' && allergy.addedby
          ? { id: allergy.addedby._id, name: allergy.addedby.name, email: allergy.addedby.email }
          : { id: allergy.addedby, name: 'Unknown', email: '' };

        return {
          // Primary identifiers
          allergy_id: allergy._id || allergy.allergy_id || allergy.id,
          
          // Allergy information
          allergen: allergy.allergen || '',
          allergy_type: allergy.allergytype || '',
          reaction: allergy.reaction || '',
          severity: allergy.severity || '',
          current_status: allergy.currentstatus || '',
          
          // Dates
          reported_on: allergy.reportedon || allergy.createdAt || allergy.reported_date,
          resolved_on: allergy.resolvedon || allergy.resolved_date || null,
          created_date: allergy.createdAt || allergy.created_date,
          updated_date: allergy.updatedAt || allergy.updated_date,
          
          // Personnel
          added_by: addedByInfo.name,
          added_by_id: addedByInfo.id,
          added_by_email: addedByInfo.email,
          
          // Additional data
          comments: allergy.comments || '',
          status: allergy.status || '1',
          
          // Patient info
          patient_id: patientInfo.id,
          patient_name: patientInfo.name,
          
          // Preserve original for detailed view
          _original: allergy
        };
      });

      console.log('Normalized allergies:', normalizedAllergies);
      
      // **IMPORTANTE: Solo actualizar si el contador cambió**
      if (allergies.length !== normalizedAllergies.length) {
        console.log(`📊 ${componentName}: Contador cambió de ${allergies.length} a ${normalizedAllergies.length}`);
        onCountUpdate && onCountUpdate(componentName, normalizedAllergies.length);
      }
      
      setAllergies(normalizedAllergies);
    } catch (error) {
      console.error('Error loading allergies:', error);
      setAllergies([]);
      // También actualizar contador en caso de error
      if (allergies.length !== 0) {
        onCountUpdate && onCountUpdate(componentName, 0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [patientId, allergies.length, onCountUpdate, componentName, patientData]);

  // **FUNCIÓN SEPARADA PARA CARGAR ESTADÍSTICAS**
  const loadAllergiesStats = useCallback(async () => {
    if (!patientId || allergies.length === 0) return;
    
    try {
      const severeCount = allergies.filter(allergy => 
        allergy.severity?.toLowerCase() === 'severe'
      ).length;
      
      const activeCount = allergies.filter(allergy => 
        allergy.current_status?.toLowerCase() === 'active' || allergy.status === '1'
      ).length;
      
      if (severeCount > 0 || activeCount > 0) {
        setAlertInfo({
          alert_type: 'allergy',
          message: `${activeCount} active allergies${severeCount > 0 ? `, ${severeCount} severe` : ''}`,
          priority: severeCount > 0 ? 'high' : 'medium',
          requires_action: severeCount > 0
        });
      } else {
        setAlertInfo(null);
      }
    } catch (error) {
      console.error('Error loading allergies stats:', error);
      setAlertInfo(null);
    }
  }, [patientId, allergies]);

const applyFilters = useCallback(() => {
  console.log('🔄 Aplicando filtros y ordenamiento...');
  console.log('Allergies array length:', allergies.length);
  console.log('Current sortBy:', sortBy);
  
  let filtered = [...allergies];

  // Aplicar filtro de búsqueda global
  if (globalFilter) {
    filtered = filtered.filter(allergy => 
      (allergy.allergen || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
      (allergy.allergy_type || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
      (allergy.reaction || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
      (allergy.severity || '').toLowerCase().includes(globalFilter.toLowerCase())
    );
  }

  // Aplicar filtros específicos
  if (filters.status !== 'All') {
    if (filters.status === 'Active') {
      filtered = filtered.filter(allergy => allergy.status === '1');
    } else if (filters.status === 'Inactive') {
      filtered = filtered.filter(allergy => allergy.status === '0');
    }
  }

  if (filters.allergyType) {
    filtered = filtered.filter(allergy => 
      (allergy.allergy_type || '').toLowerCase().includes(filters.allergyType.toLowerCase())
    );
  }

  if (filters.severity) {
    filtered = filtered.filter(allergy => 
      (allergy.severity || '').toLowerCase() === filters.severity.toLowerCase()
    );
  }

  if (filters.currentStatus) {
    filtered = filtered.filter(allergy => 
      (allergy.current_status || '').toLowerCase() === filters.currentStatus.toLowerCase()
    );
  }

  if (filters.reportedDate) {
    filtered = filtered.filter(allergy => {
      const reportedDate = allergy.reported_on;
      return reportedDate && new Date(reportedDate) >= new Date(filters.reportedDate);
    });
  }

  if (filters.endDate) {
    filtered = filtered.filter(allergy => {
      const reportedDate = allergy.reported_on;
      return reportedDate && new Date(reportedDate) <= new Date(filters.endDate);
    });
  }

  console.log('Filtered array length:', filtered.length);

  // SOLUCIÓN: Mejorar la función de ordenamiento con validaciones
  filtered.sort((a, b) => {
    console.log('Sorting with:', sortBy);
    
    const getDate = (item) => {
      const dateValue = item.reported_on || item.created_date || new Date().toISOString();
      return new Date(dateValue);
    };
    
    switch (sortBy) {
      case 'newest':
        return getDate(b) - getDate(a);
      case 'oldest':
        return getDate(a) - getDate(b);
      case 'severity': {
        const severityOrder = { 'severe': 3, 'moderate': 2, 'mild': 1 };
        const severityA = (a.severity || '').toLowerCase();
        const severityB = (b.severity || '').toLowerCase();
        return (severityOrder[severityB] || 0) - (severityOrder[severityA] || 0);
      }
      case 'alphabetical':
        const allergenA = (a.allergen || '').toLowerCase();
        const allergenB = (b.allergen || '').toLowerCase();
        return allergenA.localeCompare(allergenB);
      default:
        return 0;
    }
  });

  console.log('Final filtered array length:', filtered.length);
  setFilteredAllergies(filtered);
}, [allergies, filters, globalFilter, sortBy]);

  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
  };

  const handleSortChange = useCallback((sortOption) => {
  console.log('Cambiando orden a:', sortOption);
  setSortBy(sortOption);
  setShowSortDropdown(false);
}, []);

  const getSortDisplayText = () => {
    switch (sortBy) {
      case 'newest': return 'Newest First';
      case 'oldest': return 'Oldest First';
      case 'severity': return 'By Severity';
      case 'alphabetical': return 'A-Z';
      default: return 'Sort by';
    }
  };

  // Table columns definition - MOVED BEFORE EARLY RETURN
  const columns = useMemo(() => [
    {
      accessorKey: 'allergen',
      header: 'Allergen',
      size: 150,
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div>
            <div className="fw-bold text-primary">
              {row.original.allergen || 'Unknown Allergen'}
            </div>
            <small className="text-muted">
              {formatDate(row.original.reported_on)}
            </small>
          </div>
        </div>
      )
    },
    {
      accessorKey: 'allergy_type',
      header: 'Allergy Type',
      size: 120,
      Cell: ({ row }) => (
        <span className={`badge bg-${getAllergyTypeColor(row.original.allergy_type)}`}>
          {row.original.allergy_type || 'Not specified'}
        </span>
      )
    },
    {
      accessorKey: 'severity',
      header: 'Severity',
      size: 100,
      Cell: ({ row }) => (
        <span className={`badge bg-${getSeverityColor(row.original.severity)} d-flex align-items-center gap-1`}>
          {getSeverityIcon(row.original.severity)}
          <span className="text-capitalize">
            {row.original.severity || 'Unknown'}
          </span>
        </span>
      )
    },
    {
      accessorKey: 'reaction',
      header: 'Reaction',
      size: 200,
      Cell: ({ row }) => (
        <div>
          <div className="text-wrap">
            {row.original.reaction || 'Not specified'}
          </div>
        </div>
      )
    },
    {
      accessorKey: 'current_status',
      header: 'Current Status',
      size: 120,
      Cell: ({ row }) => (
        <div>
          <span className={`badge bg-${row.original.current_status?.toLowerCase() === 'active' ? 'success' : 'secondary'}`}>
            {row.original.current_status || 'Unknown'}
          </span>
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      Cell: ({ row }) => (
        <span className={`badge bg-${getStatusColor(row.original.status)}`}>
          {getStatusText(row.original.status)}
        </span>
      )
    },
    {
      accessorKey: 'added_by',
      header: 'Added By',
      size: 120,
      Cell: ({ row }) => (
        <div>
          <div>{row.original.added_by || 'Unknown'}</div>
          <small className="text-muted">
            {formatDate(row.original.created_date)}
          </small>
        </div>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleViewAllergy(row.original.allergy_id);
            }}
            title="View Details"
          >
            <FiEye />
          </button>
          <button
  className="btn btn-outline-secondary btn-sm"
  onClick={(e) => {
    e.stopPropagation();
    handleEditAllergy(row.original.allergy_id);
  }}
  title="Edit"
>
  <FiEdit3 />
</button>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteAllergy(row.original.allergy_id);
            }}
            title="Delete"
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ], [allergies]);

  // Redireccionar si no hay datos del paciente - MOVED AFTER HOOKS
//   useEffect(() => {
//     if (!patientData || !patientId) {
//       console.warn('No patient data found, redirecting...');
//       navigate('/patients');
//       return;
//     }
//   }, [patientData, patientId, navigate]);

  // **EFECTO PRINCIPAL PARA CARGAR DATOS**
  useEffect(() => {
    console.log(`🔄 ${componentName}: useEffect triggered, patientId:`, patientId);
    if (patientId) {
      loadAllergies();
    }
  }, [patientId, loadAllergies]);

  // **EFECTO SEPARADO PARA ESTADÍSTICAS**
  useEffect(() => {
    if (allergies.length > 0) {
      loadAllergiesStats();
    }
  }, [allergies, loadAllergiesStats]);

  // **EFECTO PARA ACTUALIZAR CONTADOR CUANDO CAMBIEN LOS DATOS**
  useEffect(() => {
    console.log(`📊 ${componentName}: Actualizando contador a ${allergies.length}`);
    onCountUpdate && onCountUpdate(componentName, allergies.length);
  }, [allergies.length, onCountUpdate, componentName]);

  // Apply filters whenever allergies or filters change
  useEffect(() => {
  console.log('📊 useEffect applyFilters triggered');
  console.log('- allergies.length:', allergies.length);
  console.log('- sortBy:', sortBy);
  
  if (allergies.length > 0) {
    applyFilters();
  } else {
    console.log('⚠️ No hay allergies, limpiando filteredAllergies');
    setFilteredAllergies([]);
  }
}, [allergies, filters, globalFilter, sortBy, applyFilters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Prevent body scroll when offcanvas are open
  useEffect(() => {
    if (showFilterOffcanvas || showAddOffcanvas || showViewOffcanvas || showEditOffcanvas) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '17px';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    };
  }, [showFilterOffcanvas, showAddOffcanvas, showViewOffcanvas]);

  // **COMPONENTE SE PUEDE MOSTRAR SIN DATOS DEL PACIENTE**
// Solo mostrar loading si estamos esperando datos específicos
if (isLoading && patientId) {
  return <div>Loading patient data...</div>;
}



  const handleViewAllergy = (allergyId) => {
    console.log('Viewing allergy:', allergyId);
    const allergy = allergies.find(a => a.allergy_id === allergyId);
    if (allergy) {
      console.log('Found allergy for viewing:', allergy);
      setSelectedAllergy(allergy);
      setShowViewOffcanvas(true);
    } else {
      console.error('Allergy not found:', allergyId);
    }
  };
  
const handleEditAllergy = (allergyId) => {
  console.log('Editing allergy:', allergyId);
  const allergy = allergies.find(a => a.allergy_id === allergyId);
  if (allergy) {
    console.log('Found allergy for editing:', allergy);
    setEditingAllergy(allergy);
    setShowEditOffcanvas(true);
  } else {
    console.error('Allergy not found for editing:', allergyId);
  }
};

  const handleAddNew = () => {
    console.log('Add New button clicked - opening modal');
    // Cerrar cualquier offcanvas abierto antes de abrir el de agregar
    setShowFilterOffcanvas(false);
    setShowViewOffcanvas(false);
    setShowAddOffcanvas(true);
  };

  // **FUNCIÓN MEJORADA PARA MANEJAR LA CREACIÓN DE ALERGIA**
const handleSubmitNewAllergy = async (allergyData) => {
  console.log('=== SUBMITTING NEW ALLERGY ===');
  console.log('Original allergy data:', allergyData);
  
  setIsSubmitting(true);
  
  try {
    // **OBTENER DATOS DEL USUARIO Y ECOSISTEMA ACTUAL**
    const currentUser = getCurrentUser();
    const currentEcosystem = getCurrentEcosystem();
    
    console.log('Current user:', currentUser);
    console.log('Current ecosystem:', currentEcosystem);
    
    // Validar que tenemos los datos necesarios
    if (!currentUser || !currentUser.id) {
      throw new Error('No se pudo obtener información del usuario actual. Por favor, inicia sesión nuevamente.');
    }
    
    if (!currentEcosystem || !currentEcosystem.id) {
      throw new Error('No se pudo obtener información del ecosistema actual. Por favor, verifica tu configuración.');
    }

    if (!patientId) {
      throw new Error('No se pudo obtener el ID del paciente.');
    }

    // Preparar datos completos para la API
    const completeAllergyData = {
      // Datos de la alergia
      allergen: allergyData.allergen || '',
      allergytype: allergyData.allergytype || '',
      reaction: allergyData.reaction || '',
      severity: allergyData.severity || '',
      currentstatus: allergyData.currentstatus || 'active',
      reportedon: allergyData.reportedon || new Date().toISOString().split('T')[0],
      resolvedon: allergyData.resolvedon || null,
      comments: allergyData.comments || '',
      
      // IDs requeridos
      patient: patientId,
      addedby: currentUser.id,
      ecosystemid: currentEcosystem.id,
      
      // Status por defecto
      status: '1'
    };

    console.log('Complete allergy data to send:', completeAllergyData);
    
    // Crear la alergia
    const result = await AllergyService.createAllergy(completeAllergyData);
    console.log('Allergy created successfully:', result);
    
    // Recargar datos para obtener el nuevo contador
    await loadAllergies();
    await loadAllergiesStats();
    
    // Cerrar modal y mostrar éxito
    setShowAddOffcanvas(false);
    
    // Opcional: mostrar mensaje de éxito
    if (window.showSuccessMessage) {
      window.showSuccessMessage('Allergy created successfully!');
    } else {
      alert('Allergy created successfully!');
    }
    
  } catch (error) {
    console.error('Error creating allergy:', error);
    
    // Mostrar error específico
    let errorMessage = 'Error creating allergy. Please try again.';
    
    if (error.message) {
      errorMessage = error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    }
    
    alert(`Error: ${errorMessage}`);
    throw error;
  } finally {
    setIsSubmitting(false);
  }
};

const handleUpdateAllergy = async (updatedData) => {
  console.log('=== UPDATING ALLERGY ===');
  console.log('Updated allergy data:', updatedData);
  console.log('Editing allergy ID:', editingAllergy?.allergy_id);
  
  setIsSubmitting(true);
  
  try {
    if (!editingAllergy?.allergy_id) {
      throw new Error('No se pudo obtener el ID de la alergia a actualizar');
    }

    // Preparar datos para actualización
    const updateData = {
      allergen: updatedData.allergen || '',
      allergytype: updatedData.allergytype || '',
      reaction: updatedData.reaction || '',
      severity: updatedData.severity || '',
      currentstatus: updatedData.currentstatus || 'active',
      reportedon: updatedData.reportedon,
      resolvedon: updatedData.resolvedon || null,
      comments: updatedData.comments || ''
    };

    console.log('Update data to send:', updateData);
    
    // Actualizar la alergia usando el service
    const result = await AllergyService.updateAllergy(editingAllergy.allergy_id, updateData);
    console.log('Allergy updated successfully:', result);
    
    // Recargar datos para obtener el nuevo estado
    await loadAllergies();
    await loadAllergiesStats();
    
    // Cerrar modal y mostrar éxito
    setShowEditOffcanvas(false);
    setEditingAllergy(null);
    
    // Mostrar mensaje de éxito
    if (window.showSuccessMessage) {
      window.showSuccessMessage('Allergy updated successfully!');
    } else {
      alert('Allergy updated successfully!');
    }
    
  } catch (error) {
    console.error('Error updating allergy:', error);
    
    let errorMessage = 'Error updating allergy. Please try again.';
    
    if (error.message) {
      errorMessage = error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    }
    
    alert(`Error: ${errorMessage}`);
    throw error;
  } finally {
    setIsSubmitting(false);
  }
};

const debugCurrentData = () => {
  console.log('=== DEBUG CURRENT DATA ===');
  console.log('localStorage keys:', Object.keys(localStorage));
  console.log('sessionStorage keys:', Object.keys(sessionStorage));
  
  // Mostrar todo el localStorage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    console.log(`localStorage.${key}:`, localStorage.getItem(key));
  }
  
  // Mostrar todo el sessionStorage
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    console.log(`sessionStorage.${key}:`, sessionStorage.getItem(key));
  }
  
  console.log('patientId:', patientId);
  console.log('patientData:', patientData);
  console.log('getCurrentUser():', getCurrentUser());
  console.log('getCurrentEcosystem():', getCurrentEcosystem());
  console.log('=== END DEBUG ===');
};

// SOLUCIÓN 4: ALTERNATIVA - HARDCODEAR VALORES TEMPORALMENTE
const handleSubmitNewAllergyWithHardcodedValues = async (allergyData) => {
  console.log('Using hardcoded values for testing...');
  setIsSubmitting(true);
  
  try {
    // **VALORES HARDCODEADOS PARA TESTING - REEMPLAZA CON TUS VALORES REALES**
    const completeAllergyData = {
      // Datos de la alergia
      allergen: allergyData.allergen || '',
      allergytype: allergyData.allergytype || '',
      reaction: allergyData.reaction || '',
      severity: allergyData.severity || '',
      currentstatus: allergyData.currentstatus || 'active',
      reportedon: allergyData.reportedon || new Date().toISOString().split('T')[0],
      resolvedon: allergyData.resolvedon || null,
      comments: allergyData.comments || '',
      
      // IDs hardcodeados - REEMPLAZA ESTOS CON TUS IDs REALES
      patient: patientId,
      addedby: '507f1f77bcf86cd799439011', // ⚠️ REEMPLAZA con un ID de usuario válido
      ecosystemid: '507f1f77bcf86cd799439012', // ⚠️ REEMPLAZA con un ID de ecosistema válido
      
      status: '1'
    };

    console.log('Complete allergy data (hardcoded):', completeAllergyData);
    
    const result = await AllergyService.createAllergy(completeAllergyData);
    console.log('Allergy created:', result);
    
    await loadAllergies();
    await loadAllergiesStats();
    setShowAddOffcanvas(false);
    alert('Allergy created successfully!');
    
  } catch (error) {
    console.error('Error creating allergy:', error);
    alert(`Error creating allergy: ${error.message || 'Unknown error'}`);
    throw error;
  } finally {
    setIsSubmitting(false);
  }
};

  // **FUNCIONES AUXILIARES PARA OBTENER DATOS DEL USUARIO**
  const getCurrentUser = () => {
  console.log('Getting current user...');
  
  // OPCIÓN 1: Desde localStorage
  try {
    const userData = localStorage.getItem('currentUser') || localStorage.getItem('user') || localStorage.getItem('authUser');
    if (userData) {
      const user = JSON.parse(userData);
      console.log('User found in localStorage:', user);
      return {
        id: user._id || user.id || user.userId,
        name: user.name || user.username || user.fullName,
        email: user.email
      };
    }
  } catch (error) {
    console.error('Error parsing user data from localStorage:', error);
  }

  // OPCIÓN 2: Desde sessionStorage
  try {
    const sessionUserData = sessionStorage.getItem('currentUser') || sessionStorage.getItem('user');
    if (sessionUserData) {
      const user = JSON.parse(sessionUserData);
      console.log('User found in sessionStorage:', user);
      return {
        id: user._id || user.id || user.userId,
        name: user.name || user.username || user.fullName,
        email: user.email
      };
    }
  } catch (error) {
    console.error('Error parsing user data from sessionStorage:', error);
  }

  // OPCIÓN 3: Desde cookies (si usas cookies)
  try {
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split('=');
      acc[key] = value;
      return acc;
    }, {});
    
    if (cookies.userId || cookies.user) {
      return {
        id: cookies.userId || JSON.parse(cookies.user).id,
        name: cookies.userName || JSON.parse(cookies.user).name || 'Current User',
        email: cookies.userEmail || JSON.parse(cookies.user).email
      };
    }
  } catch (error) {
    console.error('Error getting user from cookies:', error);
  }

  // OPCIÓN 4: Valores por defecto temporales para testing
  console.warn('No user data found, using default values for testing');
  return {
    id: '507f1f77bcf86cd799439011', // ObjectId válido para testing
    name: 'Test User',
    email: 'test@example.com'
  };
};

const getCurrentEcosystem = () => {
  console.log('Getting current ecosystem...');
  
  // OPCIÓN 1: Desde localStorage
  try {
    const ecosystemData = localStorage.getItem('currentEcosystem') || localStorage.getItem('ecosystem');
    if (ecosystemData) {
      const ecosystem = JSON.parse(ecosystemData);
      console.log('Ecosystem found in localStorage:', ecosystem);
      return {
        id: ecosystem._id || ecosystem.id || ecosystem.ecosystemId,
        name: ecosystem.name || ecosystem.ecosystemName
      };
    }
  } catch (error) {
    console.error('Error parsing ecosystem data from localStorage:', error);
  }

  // OPCIÓN 2: Desde sessionStorage
  try {
    const sessionEcosystemData = sessionStorage.getItem('currentEcosystem') || sessionStorage.getItem('ecosystem');
    if (sessionEcosystemData) {
      const ecosystem = JSON.parse(sessionEcosystemData);
      console.log('Ecosystem found in sessionStorage:', ecosystem);
      return {
        id: ecosystem._id || ecosystem.id || ecosystem.ecosystemId,
        name: ecosystem.name || ecosystem.ecosystemName
      };
    }
  } catch (error) {
    console.error('Error parsing ecosystem data from sessionStorage:', error);
  }

  // OPCIÓN 3: Valores por defecto temporales para testing
  console.warn('No ecosystem data found, using default values for testing');
  return {
    id: '507f1f77bcf86cd799439012', // ObjectId válido para testing
    name: 'Test Ecosystem'
  };
};

  const handleDeleteAllergy = async (allergyId) => {
    if (window.confirm('Are you sure you want to delete this allergy?')) {
      try {
        await AllergyService.deleteAllergy(allergyId);
        await loadAllergies();
        await loadAllergiesStats();
      } catch (error) {
        console.error('Error deleting allergy:', error);
        alert('Error deleting allergy. Please try again.');
      }
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleApplyFilters = () => {
    setShowFilterOffcanvas(false);
  };

  const handleClearFilters = () => {
    setFilters({
      allergyType: '',
      severity: '',
      currentStatus: '',
      reportedDate: '',
      endDate: '',
      status: 'All'
    });
  };

  const handleCloseViewOffcanvas = () => {
    console.log('Closing view offcanvas');
    setShowViewOffcanvas(false);
    setSelectedAllergy(null);
  };

  const handleCloseAddOffcanvas = () => {
    console.log('Closing add offcanvas');
    if (!isSubmitting) {
      setShowAddOffcanvas(false);
    }
  };

  const handleCloseEditOffcanvas = () => {
  console.log('Closing edit offcanvas');
  if (!isSubmitting) {
    setShowEditOffcanvas(false);
    setEditingAllergy(null);
  }
};

  const handleCloseFilterOffcanvas = () => {
    setShowFilterOffcanvas(false);
  };

  const handleOpenFilterOffcanvas = () => {
    // Cerrar cualquier offcanvas abierto antes de abrir el de filtros
    setShowAddOffcanvas(false);
    setShowViewOffcanvas(false);
    setShowFilterOffcanvas(true);
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'severe':
        return 'danger';
      case 'moderate':
        return 'warning';
      case 'mild':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'severe':
        return <FiAlertTriangle />;
      case 'moderate':
        return <FiInfo />;
      case 'mild':
        return <FiCheck />;
      default:
        return <FiInfo />;
    }
  };

  const getAllergyTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'food':
        return 'primary';
      case 'drug':
        return 'danger';
      case 'environmental':
        return 'success';
      case 'other':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getStatusColor = (status) => {
    return status === '1' ? 'success' : 'secondary';
  };

  const getStatusText = (status) => {
    return status === '1' ? 'Active' : 'Inactive';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getActiveFilters = () => {
    const activeFilters = [];
    
    if (globalFilter) {
      activeFilters.push({
        key: 'search',
        label: `Search: "${globalFilter}"`,
        value: globalFilter,
        type: 'search'
      });
    }
    
    if (filters.status !== 'All') {
      activeFilters.push({
        key: 'status',
        label: `Status: ${filters.status}`,
        value: filters.status,
        type: 'status'
      });
    }
    
    if (filters.allergyType) {
      activeFilters.push({
        key: 'allergyType',
        label: `Type: ${filters.allergyType}`,
        value: filters.allergyType,
        type: 'allergyType'
      });
    }
    
    if (filters.severity) {
      activeFilters.push({
        key: 'severity',
        label: `Severity: ${filters.severity}`,
        value: filters.severity,
        type: 'severity'
      });
    }
    
    if (filters.currentStatus) {
      activeFilters.push({
        key: 'currentStatus',
        label: `Current Status: ${filters.currentStatus}`,
        value: filters.currentStatus,
        type: 'currentStatus'
      });
    }
    
    if (filters.reportedDate) {
      activeFilters.push({
        key: 'reportedDate',
        label: `From: ${formatDate(filters.reportedDate)}`,
        value: filters.reportedDate,
        type: 'reportedDate'
      });
    }
    
    if (filters.endDate) {
      activeFilters.push({
        key: 'endDate',
        label: `To: ${formatDate(filters.endDate)}`,
        value: filters.endDate,
        type: 'endDate'
      });
    }
    
    if (sortBy !== 'newest') {
      activeFilters.push({
        key: 'sort',
        label: `Sort: ${getSortDisplayText()}`,
        value: sortBy,
        type: 'sort'
      });
    }
    
    return activeFilters;
  };

  const handleRemoveFilter = (filterToRemove) => {
    switch (filterToRemove.type) {
      case 'search':
        setGlobalFilter('');
        break;
      case 'status':
        setFilters(prev => ({ ...prev, status: 'All' }));
        break;
      case 'allergyType':
        setFilters(prev => ({ ...prev, allergyType: '' }));
        break;
      case 'severity':
        setFilters(prev => ({ ...prev, severity: '' }));
        break;
      case 'currentStatus':
        setFilters(prev => ({ ...prev, currentStatus: '' }));
        break;
      case 'reportedDate':
        setFilters(prev => ({ ...prev, reportedDate: '' }));
        break;
      case 'endDate':
        setFilters(prev => ({ ...prev, endDate: '' }));
        break;
      case 'sort':
        setSortBy('newest');
        break;
      default:
        break;
    }
  };

  const handleClearAllActiveFilters = () => {
    setGlobalFilter('');
    setFilters({
      allergyType: '',
      severity: '',
      currentStatus: '',
      reportedDate: '',
      endDate: '',
      status: 'All'
    });
    setSortBy('newest');
  };

  return (
    <main id="app">
      <TopHeader />
      <div className="container-fluid w-100">
        <PatientManagement activeMenu='/my-allergies'/>
        
        {/* Breadcrumb */}
        <div className="mb-3">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
              <li className="breadcrumb-item"><a href="/patients/list">Patients</a></li>
              <li className="breadcrumb-item active" aria-current="page">
  My Allergies {patientData?.patientName ? `- ${patientData.patientName}` : ''}
</li>
            </ol>
          </nav>
        </div>

        <div className="card-body">
          {/* Enhanced Search and Actions Bar */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between">
                
                {/* Left side - Search */}
                <div className="d-flex align-items-center gap-2">
                  <div className="input-group" style={{maxWidth: '300px'}}>
                    <span className="input-group-text">
                      <FiSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search"
                      value={globalFilter}
                      onChange={(e) => handleGlobalFilterChange(e.target.value)}
                    />
                  </div>
                </div>

                {/* Right side - Controls */}
                <div className="d-flex align-items-center gap-2">
                  {/* Sort By Dropdown */}
                  <div className="dropdown" style={{position: 'relative'}}>
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center gap-2"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSortDropdown(!showSortDropdown);
                      }}
                    >
                      {getSortDisplayText()}
                      <FiChevronDown />
                    </button>
                    {showSortDropdown && (
                      <div className="dropdown-menu show" style={{position: 'absolute', top: '100%', right: 0, zIndex: 1000}}>
                        <button
                          className={`dropdown-item ${sortBy === 'newest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('newest')}
                        >
                          Newest First
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'oldest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('oldest')}
                        >
                          Oldest First
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'severity' ? 'active' : ''}`}
                          onClick={() => handleSortChange('severity')}
                        >
                          By Severity
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'alphabetical' ? 'active' : ''}`}
                          onClick={() => handleSortChange('alphabetical')}
                        >
                          A-Z
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Filter Button */}
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={handleOpenFilterOffcanvas}
                  >
                    <FiFilter />
                    Filter
                  </button>
            
                  {/* Add New Button */}
                  <button
                    className="btn btn-primary d-flex align-items-center gap-2"
                    onClick={handleAddNew}
                  >
                    <FiPlus />
                    Add New
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {getActiveFilters().length > 0 && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex align-items-center gap-2 p-3 bg-light rounded">
                  <span className="text-muted fw-medium">Active filters:</span>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {getActiveFilters().map((filter) => (
                      <span
                        key={filter.key}
                        className="badge bg-primary d-flex align-items-center gap-1"
                        style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                      >
                        {filter.label}
                        <button
                          type="button"
                          className="btn-close btn-close-white"
                          style={{ 
                            fontSize: '0.625rem',
                            padding: '0.125rem',
                            marginLeft: '0.25rem'
                          }}
                          onClick={() => handleRemoveFilter(filter)}
                          title={`Remove ${filter.label} filter`}
                        ></button>
                      </span>
                    ))}
                    
                    {/* Clear All Button */}
                    {getActiveFilters().length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                        onClick={handleClearAllActiveFilters}
                        title="Clear all filters"
                      >
                        <FiX size={14} />
                        Clear All
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Table */}
<MaterialReactTable
  columns={columns}
  data={filteredAllergies}
  
  // Configuración básica
  enableRowSelection
  enableColumnFilters
  enableGlobalFiltering
  enableSorting={false}
  enablePagination
  
  // Toolbars
  enableTopToolbar
  enableBottomToolbar
  
  // Features opcionales (puedes deshabilitarlas si no las necesitas)
  enableDensityToggle
  enableFullScreenToggle
  enableHiding
  enableColumnOrdering
  enableColumnResizing={false}  // Deshabilitar si causa problemas
  enableGrouping={false}        // Deshabilitar si causa problemas
  
  // Estados
  initialState={{
    pagination: { pageSize: 10, pageIndex: 0 },
    sorting: [{ id: 'allergen', desc: false }], // Cambiar a columna existente
    density: 'comfortable',
    showGlobalFilter: false
  }}
  
  state={{
    isLoading,
    rowSelection,
    globalFilter
  }}
  
  // Callbacks
  onRowSelectionChange={setRowSelection}
  onGlobalFilterChange={handleGlobalFilterChange}
  
  // Configuración de renderizado
  muiTableContainerProps={{
    sx: { maxHeight: '600px' }
  }}
          />

          {/* Results Summary */}
          {filteredAllergies.length > 0 && (
            <div className="mt-3">
              <small className="text-muted">
                Showing {filteredAllergies.length} of {allergies.length} allergies
                {Object.values(filters).some(f => f && f !== 'All') || globalFilter ? ' (filtered)' : ''}
              </small>
            </div>
          )}
        </div>
      </div>

      {/* Filter Offcanvas */}
      {showFilterOffcanvas && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseFilterOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1">
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Filter Allergies</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseFilterOffcanvas}
              ></button>
            </div>
            <div className="offcanvas-body">
              <form>
                {/* Status Filter */}
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="All">All</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                {/* Allergy Type Filter */}
                <div className="mb-3">
                  <label className="form-label">Allergy Type</label>
                  <select
                    className="form-select"
                    value={filters.allergyType}
                    onChange={(e) => handleFilterChange('allergyType', e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="food">Food</option>
                    <option value="drug">Drug</option>
                    <option value="environmental">Environmental</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Severity Filter */}
                <div className="mb-3">
                  <label className="form-label">Severity</label>
                  <select
                    className="form-select"
                    value={filters.severity}
                    onChange={(e) => handleFilterChange('severity', e.target.value)}
                  >
                    <option value="">All Severities</option>
                    <option value="mild">Mild</option>
                    <option value="moderate">Moderate</option>
                    <option value="severe">Severe</option>
                  </select>
                </div>

                {/* Current Status Filter */}
                <div className="mb-3">
                  <label className="form-label">Current Status</label>
                  <select
                    className="form-select"
                    value={filters.currentStatus}
                    onChange={(e) => handleFilterChange('currentStatus', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="resolved">Resolved</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {/* Date Range Filters */}
                <div className="mb-3">
                  <label className="form-label">Reported From</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.reportedDate}
                    onChange={(e) => handleFilterChange('reportedDate', e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Reported To</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary flex-fill"
                    onClick={handleApplyFilters}
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-fill"
                    onClick={handleClearFilters}
                  >
                    Clear All
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* View Allergy Offcanvas */}
      {showViewOffcanvas && selectedAllergy && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseViewOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '600px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Allergy Details</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseViewOffcanvas}
              ></button>
            </div>
            <div className="offcanvas-body">
              <div className="row g-3">
                {/* Basic Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Basic Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Allergen</label>
                          <p className="form-control-plaintext">
                            {selectedAllergy.allergen || 'Not specified'}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Allergy Type</label>
                          <p className="form-control-plaintext">
                            <span className={`badge bg-${getAllergyTypeColor(selectedAllergy.allergy_type)}`}>
                              {selectedAllergy.allergy_type || 'Not specified'}
                            </span>
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Severity</label>
                          <p className="form-control-plaintext">
                            <span className={`badge bg-${getSeverityColor(selectedAllergy.severity)} d-flex align-items-center gap-1`} style={{width: 'fit-content'}}>
                              {getSeverityIcon(selectedAllergy.severity)}
                              {selectedAllergy.severity || 'Not specified'}
                            </span>
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Current Status</label>
                          <p className="form-control-plaintext">
                            <span className={`badge bg-${selectedAllergy.current_status?.toLowerCase() === 'active' ? 'success' : 'secondary'}`}>
                              {selectedAllergy.current_status || 'Not specified'}
                            </span>
                          </p>
                        </div>
                        <div className="col-12">
                          <label className="form-label fw-bold">Reaction</label>
                          <p className="form-control-plaintext">
                            {selectedAllergy.reaction || 'Not specified'}
                          </p>
                        </div>
                        {selectedAllergy.comments && (
                          <div className="col-12">
                            <label className="form-label fw-bold">Comments</label>
                            <p className="form-control-plaintext">
                              {selectedAllergy.comments}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Date Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Date Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            <FiCalendar className="me-1" />
                            Reported On
                          </label>
                          <p className="form-control-plaintext">
                            {formatDate(selectedAllergy.reported_on)}
                          </p>
                        </div>
                        {selectedAllergy.resolved_on && (
                          <div className="col-md-6">
                            <label className="form-label fw-bold">
                              <FiCalendar className="me-1" />
                              Resolved On
                            </label>
                            <p className="form-control-plaintext">
                              {formatDate(selectedAllergy.resolved_on)}
                            </p>
                          </div>
                        )}
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Created</label>
                          <p className="form-control-plaintext">
                            {formatDateTime(selectedAllergy.created_date)}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Last Updated</label>
                          <p className="form-control-plaintext">
                            {formatDateTime(selectedAllergy.updated_date)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Personnel Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Personnel Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            <FiUser className="me-1" />
                            Added By
                          </label>
                          <p className="form-control-plaintext">
                            {selectedAllergy.added_by || 'Unknown'}
                          </p>
                          {selectedAllergy.added_by_email && (
                            <small className="text-muted">
                              {selectedAllergy.added_by_email}
                            </small>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Status</label>
                          <p className="form-control-plaintext">
                            <span className={`badge bg-${getStatusColor(selectedAllergy.status)}`}>
                              {getStatusText(selectedAllergy.status)}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="col-12">
                  <div className="d-flex gap-2">
                    <button 
  className="btn btn-outline-primary flex-fill"
  onClick={() => {
    handleCloseViewOffcanvas();
    setTimeout(() => handleEditAllergy(selectedAllergy.allergy_id), 300);
  }}
>
  <FiEdit3 className="me-2" />
  Edit Allergy
</button>
                    <button 
                      className="btn btn-outline-danger flex-fill"
                      onClick={() => handleDeleteAllergy(selectedAllergy.allergy_id)}
                    >
                      <FiTrash2 className="me-2" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add New Allergy Offcanvas */}
      {showAddOffcanvas && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseAddOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '600px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Add New Allergy</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseAddOffcanvas}
                disabled={isSubmitting}
              ></button>
            </div>
            <div className="offcanvas-body">
              <AddAllergyForm
                onSubmit={handleSubmitNewAllergy}
                onCancel={handleCloseAddOffcanvas}
                isSubmitting={isSubmitting}
                patientData={patientData}
              />
            </div>
          </div>
        </>
      )}

      {/* Edit Allergy Offcanvas */}
{showEditOffcanvas && editingAllergy && (
  <>
    <div className="offcanvas-backdrop fade show" onClick={handleCloseEditOffcanvas}></div>
    <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '600px'}}>
      <div className="offcanvas-header">
        <h5 className="offcanvas-title">Edit Allergy</h5>
        <button
          type="button"
          className="btn-close"
          onClick={handleCloseEditOffcanvas}
          disabled={isSubmitting}
        ></button>
      </div>
      <div className="offcanvas-body">
        <EditAllergyForm
          allergy={editingAllergy}
          onSubmit={handleUpdateAllergy}
          onCancel={handleCloseEditOffcanvas}
          isSubmitting={isSubmitting}
          patientData={patientData}
        />
      </div>
    </div>
  </>
)}

      {/* Patient Clinical Data Component */}
      <PatientMedicalClinicalData 
        patientData={patientData}
        alertInfo={alertInfo}
      />
    </main>
  );
};

// AddAllergyForm Component
const AddAllergyForm = ({ onSubmit, onCancel, isSubmitting, patientData }) => {
  const [formData, setFormData] = useState({
    allergen: '',
    allergytype: '',
    reaction: '',
    severity: '',
    currentstatus: 'active',
    reportedon: new Date().toISOString().split('T')[0],
    resolvedon: '',
    comments: ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.allergen.trim()) {
      newErrors.allergen = 'Allergen name is required';
    }

    if (!formData.allergytype) {
      newErrors.allergytype = 'Allergy type is required';
    }

    if (!formData.severity) {
      newErrors.severity = 'Severity is required';
    }

    if (!formData.reportedon) {
      newErrors.reportedon = 'Reported date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      // Reset form on success
      setFormData({
        allergen: '',
        allergytype: '',
        reaction: '',
        severity: '',
        currentstatus: 'active',
        reportedon: new Date().toISOString().split('T')[0],
        resolvedon: '',
        comments: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="row g-3">
        {/* Patient Info */}
        <div className="col-12">
          <div className="alert alert-info">
            <FiUser className="me-2" />
            Adding allergy for: <strong>{patientData.patientName}</strong>
          </div>
        </div>

        {/* Allergen */}
        <div className="col-12">
          <label className="form-label required">Allergen *</label>
          <input
            type="text"
            className={`form-control ${errors.allergen ? 'is-invalid' : ''}`}
            value={formData.allergen}
            onChange={(e) => handleInputChange('allergen', e.target.value)}
            placeholder="e.g., Peanuts, Penicillin, Pollen"
            disabled={isSubmitting}
          />
          {errors.allergen && <div className="invalid-feedback">{errors.allergen}</div>}
        </div>

        {/* Allergy Type */}
        <div className="col-md-6">
          <label className="form-label required">Allergy Type *</label>
          <select
            className={`form-select ${errors.allergytype ? 'is-invalid' : ''}`}
            value={formData.allergytype}
            onChange={(e) => handleInputChange('allergytype', e.target.value)}
            disabled={isSubmitting}
          >
            <option value="">Select type</option>
            <option value="food">Food</option>
            <option value="drug">Drug</option>
            <option value="environmental">Environmental</option>
            <option value="other">Other</option>
          </select>
          {errors.allergytype && <div className="invalid-feedback">{errors.allergytype}</div>}
        </div>

        {/* Severity */}
        <div className="col-md-6">
          <label className="form-label required">Severity *</label>
          <select
            className={`form-select ${errors.severity ? 'is-invalid' : ''}`}
            value={formData.severity}
            onChange={(e) => handleInputChange('severity', e.target.value)}
            disabled={isSubmitting}
          >
            <option value="">Select severity</option>
            <option value="mild">Mild</option>
            <option value="moderate">Moderate</option>
            <option value="severe">Severe</option>
          </select>
          {errors.severity && <div className="invalid-feedback">{errors.severity}</div>}
        </div>

        {/* Current Status */}
        <div className="col-md-6">
          <label className="form-label">Current Status</label>
          <select
            className="form-select"
            value={formData.currentstatus}
            onChange={(e) => handleInputChange('currentstatus', e.target.value)}
            disabled={isSubmitting}
          >
            <option value="active">Active</option>
            <option value="resolved">Resolved</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Reported Date */}
        <div className="col-md-6">
          <label className="form-label required">Reported Date *</label>
          <input
            type="date"
            className={`form-control ${errors.reportedon ? 'is-invalid' : ''}`}
            value={formData.reportedon}
            onChange={(e) => handleInputChange('reportedon', e.target.value)}
            disabled={isSubmitting}
          />
          {errors.reportedon && <div className="invalid-feedback">{errors.reportedon}</div>}
        </div>

        {/* Resolved Date */}
        {formData.currentstatus === 'resolved' && (
          <div className="col-md-6">
            <label className="form-label">Resolved Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.resolvedon}
              onChange={(e) => handleInputChange('resolvedon', e.target.value)}
              disabled={isSubmitting}
            />
          </div>
        )}

        {/* Reaction */}
        <div className="col-12">
          <label className="form-label">Reaction</label>
          <textarea
            className="form-control"
            rows="3"
            value={formData.reaction}
            onChange={(e) => handleInputChange('reaction', e.target.value)}
            placeholder="Describe the allergic reaction"
            disabled={isSubmitting}
          />
        </div>

        {/* Comments */}
        <div className="col-12">
          <label className="form-label">Additional Comments</label>
          <textarea
            className="form-control"
            rows="3"
            value={formData.comments}
            onChange={(e) => handleInputChange('comments', e.target.value)}
            placeholder="Any additional notes or comments"
            disabled={isSubmitting}
          />
        </div>

        {/* Action Buttons */}
        <div className="col-12">
          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Creating...
                </>
              ) : (
                <>
                  <FiCheck className="me-2" />
                  Create Allergy
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

// EditAllergyForm Component
const EditAllergyForm = ({ allergy, onSubmit, onCancel, isSubmitting, patientData }) => {
  const [formData, setFormData] = useState({
    allergen: allergy.allergen || '',
    allergytype: allergy.allergy_type || '',
    reaction: allergy.reaction || '',
    severity: allergy.severity || '',
    currentstatus: allergy.current_status || 'active',
    reportedon: allergy.reported_on ? new Date(allergy.reported_on).toISOString().split('T')[0] : '',
    resolvedon: allergy.resolved_on ? new Date(allergy.resolved_on).toISOString().split('T')[0] : '',
    comments: allergy.comments || ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.allergen.trim()) {
      newErrors.allergen = 'Allergen name is required';
    }

    if (!formData.allergytype) {
      newErrors.allergytype = 'Allergy type is required';
    }

    if (!formData.severity) {
      newErrors.severity = 'Severity is required';
    }

    if (!formData.reportedon) {
      newErrors.reportedon = 'Reported date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting edit form:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="row g-3">
        {/* Patient Info */}
        <div className="col-12">
          <div className="alert alert-info">
            <FiUser className="me-2" />
            Editing allergy for: <strong>{patientData.patientName}</strong>
          </div>
        </div>

        {/* Allergen */}
        <div className="col-12">
          <label className="form-label required">Allergen *</label>
          <input
            type="text"
            className={`form-control ${errors.allergen ? 'is-invalid' : ''}`}
            value={formData.allergen}
            onChange={(e) => handleInputChange('allergen', e.target.value)}
            placeholder="e.g., Peanuts, Penicillin, Pollen"
            disabled={isSubmitting}
          />
          {errors.allergen && <div className="invalid-feedback">{errors.allergen}</div>}
        </div>

        {/* Allergy Type */}
        <div className="col-md-6">
          <label className="form-label required">Allergy Type *</label>
          <select
            className={`form-select ${errors.allergytype ? 'is-invalid' : ''}`}
            value={formData.allergytype}
            onChange={(e) => handleInputChange('allergytype', e.target.value)}
            disabled={isSubmitting}
          >
            <option value="">Select type</option>
            <option value="food">Food</option>
            <option value="drug">Drug</option>
            <option value="environmental">Environmental</option>
            <option value="other">Other</option>
          </select>
          {errors.allergytype && <div className="invalid-feedback">{errors.allergytype}</div>}
        </div>

        {/* Severity */}
        <div className="col-md-6">
          <label className="form-label required">Severity *</label>
          <select
            className={`form-select ${errors.severity ? 'is-invalid' : ''}`}
            value={formData.severity}
            onChange={(e) => handleInputChange('severity', e.target.value)}
            disabled={isSubmitting}
          >
            <option value="">Select severity</option>
            <option value="mild">Mild</option>
            <option value="moderate">Moderate</option>
            <option value="severe">Severe</option>
          </select>
          {errors.severity && <div className="invalid-feedback">{errors.severity}</div>}
        </div>

        {/* Current Status */}
        <div className="col-md-6">
          <label className="form-label">Current Status</label>
          <select
            className="form-select"
            value={formData.currentstatus}
            onChange={(e) => handleInputChange('currentstatus', e.target.value)}
            disabled={isSubmitting}
          >
            <option value="active">Active</option>
            <option value="resolved">Resolved</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Reported Date */}
        <div className="col-md-6">
          <label className="form-label required">Reported Date *</label>
          <input
            type="date"
            className={`form-control ${errors.reportedon ? 'is-invalid' : ''}`}
            value={formData.reportedon}
            onChange={(e) => handleInputChange('reportedon', e.target.value)}
            disabled={isSubmitting}
          />
          {errors.reportedon && <div className="invalid-feedback">{errors.reportedon}</div>}
        </div>

        {/* Resolved Date */}
        {formData.currentstatus === 'resolved' && (
          <div className="col-md-6">
            <label className="form-label">Resolved Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.resolvedon}
              onChange={(e) => handleInputChange('resolvedon', e.target.value)}
              disabled={isSubmitting}
            />
          </div>
        )}

        {/* Reaction */}
        <div className="col-12">
          <label className="form-label">Reaction</label>
          <textarea
            className="form-control"
            rows="3"
            value={formData.reaction}
            onChange={(e) => handleInputChange('reaction', e.target.value)}
            placeholder="Describe the allergic reaction"
            disabled={isSubmitting}
          />
        </div>

        {/* Comments */}
        <div className="col-12">
          <label className="form-label">Additional Comments</label>
          <textarea
            className="form-control"
            rows="3"
            value={formData.comments}
            onChange={(e) => handleInputChange('comments', e.target.value)}
            placeholder="Any additional notes or comments"
            disabled={isSubmitting}
          />
        </div>

        {/* Action Buttons */}
        <div className="col-12">
          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Updating...
                </>
              ) : (
                <>
                  <FiCheck className="me-2" />
                  Update Allergy
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AllergiesTreatments;